import { BarChartOption } from '@creditsights/styleguide-web';
import {
  DeteriorationImprovementMetric,
  DifferentialMetric,
  HighLowCreditRiskLevelMetric,
  RiskProductKeyMetric,
} from '../../../../services/dtos/riskProducts/creditRiskLevels.dto';
import {
  RiskDistributionDto,
  RiskDistributionGroupBy,
  RiskDistributionMetric,
} from '../../../../services/dtos/riskProducts/riskDistribution.dto';
import {
  calculateBarChartAxisLabelWidth,
  getBarChartBaseConfig,
  getColor,
  getTooltipMarkerHtml,
} from '../../../../utils/chartUtils';

export interface RiskDistributionChartData {
  series: { name: RiskProductKeyMetric; data: number[][] }[];
  xAxis: string[];
}

type MetricRecord<T extends string> = Record<T, number[]>;
type MetricWithKeyRecord<T extends string> = { key: string } & Partial<MetricRecord<T>>;

const mapByMetric = <T extends string>(
  data: RiskDistributionDto[],
  metricRecord: MetricWithKeyRecord<T>,
  metrics: T[]
): RiskDistributionChartData => {
  const source = data.reduce((acc, { key, metric, companyIds }) => {
    const existingGroup = acc.find((group) => group.key === key);
    if (existingGroup) {
      existingGroup[metric] = companyIds;
    } else {
      const newGroup = {
        ...metricRecord,
        key,
        [metric]: companyIds,
      };
      acc.push(newGroup);
    }
    return acc;
  }, [] as MetricWithKeyRecord<T>[]);

  const series = metrics.map((metric) => {
    const dataPoints: number[][] = source.map((group) => group[metric] || []);
    return {
      name: metric,
      data: dataPoints,
    };
  });

  const xAxis = source.map((group) => group.key);

  return {
    series,
    xAxis,
  };
};

const highLowCreditRiskLevelMetricDefault: MetricWithKeyRecord<HighLowCreditRiskLevelMetric> = {
  key: '',
  [HighLowCreditRiskLevelMetric.VERY_HIGH]: [],
  [HighLowCreditRiskLevelMetric.HIGH]: [],
  [HighLowCreditRiskLevelMetric.MODERATE]: [],
  [HighLowCreditRiskLevelMetric.LOW]: [],
  [HighLowCreditRiskLevelMetric.VERY_LOW]: [],
};

const deteriorationImprovementMetricDefault: MetricWithKeyRecord<DeteriorationImprovementMetric> = {
  key: '',
  [DeteriorationImprovementMetric.STRONG_DETERIORATION]: [],
  [DeteriorationImprovementMetric.MODERATE_DETERIORATION]: [],
  [DeteriorationImprovementMetric.STABLE]: [],
  [DeteriorationImprovementMetric.MODERATE_IMPROVEMENT]: [],
  [DeteriorationImprovementMetric.STRONG_IMPROVEMENT]: [],
};

const differentialMetricDefault: MetricWithKeyRecord<DifferentialMetric> = {
  key: '',
  [DifferentialMetric.HIGH_NEGATIVE_DIFFERENTIAL]: [],
  [DifferentialMetric.MODERATE_NEGATIVE_DIFFERENTIAL]: [],
  [DifferentialMetric.NEUTRAL_DIFFERENTIAL]: [],
  [DifferentialMetric.MODERATE_POSITIVE_DIFFERENTIAL]: [],
  [DifferentialMetric.HIGH_POSITIVE_DIFFERENTIAL]: [],
};

export const getTransformedData = (
  metric?: RiskDistributionMetric,
  data?: RiskDistributionDto[]
): RiskDistributionChartData | undefined => {
  if (!data || !metric) {
    return undefined;
  }
  switch (metric) {
    case RiskDistributionMetric.CRL: {
      const metrics = Object.values(HighLowCreditRiskLevelMetric).filter(
        (m) => m !== HighLowCreditRiskLevelMetric.NOT_AVAILABLE
      );
      return mapByMetric<HighLowCreditRiskLevelMetric>(data, highLowCreditRiskLevelMetricDefault, metrics);
    }
    case RiskDistributionMetric.CQS:
    case RiskDistributionMetric.CRT_6M: {
      return mapByMetric<DeteriorationImprovementMetric>(
        data,
        deteriorationImprovementMetricDefault,
        Object.values(DeteriorationImprovementMetric)
      );
    }
    case RiskDistributionMetric.CQD: {
      const metrics = Object.values(DifferentialMetric).filter((m) => m !== DifferentialMetric.NOT_AVAILABLE);
      return mapByMetric<DifferentialMetric>(data, differentialMetricDefault, metrics);
    }
    default:
      return undefined;
  }
};

const getTooltipHtml = (params) =>
  `${params[0].name}
   ${params
     .map(
       (bar) =>
         `<div>${getTooltipMarkerHtml(bar.color)}<span style="margin-left: 2px;">${
           bar.seriesName
         }</span><span style="float:right;margin-left:20px;">${bar.value}</span></div>`
     )
     .join('')}`;

export const getChartConfig = (
  chartData: RiskDistributionChartData | undefined,
  widgetWidth: number
): BarChartOption | undefined => {
  if (!chartData) {
    return undefined;
  }

  const baseConfig = getBarChartBaseConfig();
  return {
    ...baseConfig,
    tooltip: {
      ...baseConfig.tooltip,
      formatter: (params) => getTooltipHtml(params),
    },
    xAxis: [
      {
        type: 'category',
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          overflow: 'truncate',
          width: calculateBarChartAxisLabelWidth(chartData.xAxis.length, widgetWidth),
        },
        data: chartData.xAxis,
      },
    ],
    series: chartData.series.map((data) => ({
      name: data.name,
      data: data.data.map((d) => d.length),
      type: 'bar',
      emphasis: {
        focus: 'series',
      },
      color: getColor(data.name),
      itemStyle: {
        opacity: 0.8,
      },
    })),
  };
};

const RISK_DISTRIBUTION_WIDGET_NAMES = {
  DISTRIBUTION_BY_SECTOR: 'Risk Distribution by Sector: ',
  DISTRIBUTION_BY_ACR: 'Risk Distribution by ACR: ',
};

const RiskDistributionMetricLabels: { [key in RiskDistributionMetric]: string } = {
  [RiskDistributionMetric.CRL]: 'CRL',
  [RiskDistributionMetric.CQS]: 'CQS Trend 1Y',
  [RiskDistributionMetric.CRT_6M]: 'CRT 6M',
  [RiskDistributionMetric.CQD]: 'CQD',
};

export const riskDistributionWidgetNameGet = (
  selectedGroupBy: RiskDistributionGroupBy,
  riskDistributonMetric?: RiskDistributionMetric
): string => {
  let widgetName = '';
  if (selectedGroupBy === RiskDistributionGroupBy.SECTOR) {
    widgetName = RISK_DISTRIBUTION_WIDGET_NAMES.DISTRIBUTION_BY_SECTOR;
  } else if (selectedGroupBy === RiskDistributionGroupBy.RATING) {
    widgetName = RISK_DISTRIBUTION_WIDGET_NAMES.DISTRIBUTION_BY_ACR;
  }
  if (riskDistributonMetric) {
    widgetName += RiskDistributionMetricLabels[riskDistributonMetric];
  }
  return widgetName;
};
